import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AdminContextType {
  isAuthenticated: boolean;
  login: (username: string, password: string) => boolean;
  logout: () => void;
  changePassword: (oldPassword: string, newPassword: string) => boolean;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  // Check for existing admin session on mount
  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    const adminExpiry = localStorage.getItem('adminExpiry');
    
    if (adminToken && adminExpiry) {
      const now = new Date().getTime();
      const expiry = parseInt(adminExpiry);
      
      if (now < expiry) {
        setIsAuthenticated(true);
      } else {
        // Token expired, clear storage
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminExpiry');
      }
    }
  }, []);

  const login = (username: string, password: string): boolean => {
    // Get stored admin credentials (for password changes)
    const storedPassword = localStorage.getItem('adminPassword') || 'smfahad';
    
    if (username === 'admin' && password === storedPassword) {
      const token = 'admin_' + new Date().getTime();
      const expiry = new Date().getTime() + (24 * 60 * 60 * 1000); // 24 hours
      
      localStorage.setItem('adminToken', token);
      localStorage.setItem('adminExpiry', expiry.toString());
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminExpiry');
    setIsAuthenticated(false);
  };

  const changePassword = (oldPassword: string, newPassword: string): boolean => {
    const currentPassword = localStorage.getItem('adminPassword') || 'smfahad';
    
    if (oldPassword === currentPassword) {
      localStorage.setItem('adminPassword', newPassword);
      return true;
    }
    return false;
  };

  const value: AdminContextType = {
    isAuthenticated,
    login,
    logout,
    changePassword
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};